import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'

import { Tooltip, cn } from '@aeris/ui'

import type { ModuleEntry } from '../../lib/modules.tsx'
import { useVisibleModules } from '../../lib/use-visible-modules.ts'
import { useWorkspace, type WorkspaceModule } from '../../lib/workspace.ts'

/**
 * A barra de ícones do Modo PC — F138.
 *
 * As telas que o balcão usa o dia inteiro, a um clique, com ícone e nome: é o
 * que a Elite tem no GDOOR. Fixa nesta entrega, na ordem de um dia de
 * trabalho — começar, vender, receber, cadastrar, conferir. Personalizar fica
 * para depois (decisão do plano aprovado).
 *
 * Só o que a pessoa pode abrir aparece: a mesma regra da barra lateral e da
 * busca (`useVisibleModules`).
 *
 * Shift+clique abre OUTRA janela da tela, para dois cadastros lado a lado.
 */

const TOOLBAR: readonly WorkspaceModule[] = [
  'inicio',
  'pdv',
  'orcamentos',
  'vendas',
  'caixa',
  'produtos',
  'pessoas',
  'estoque',
  'financeiro',
]

/**
 * Setas andam entre os botões, como numa barra de ferramentas de verdade
 * (`role="toolbar"`): Tab entra e sai da barra de uma vez, em vez de parar em
 * nove botões seguidos.
 */
const moveFocus = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
  const step =
    event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
  const edge =
    event.key === 'Home' ? 'first' : event.key === 'End' ? 'last' : null
  if (step === 0 && edge === null) return

  const buttons = [
    ...event.currentTarget.querySelectorAll<HTMLButtonElement>('button'),
  ]
  if (buttons.length === 0) return
  const current = buttons.indexOf(document.activeElement as HTMLButtonElement)

  const next =
    edge === 'first'
      ? 0
      : edge === 'last'
        ? buttons.length - 1
        : (current + step + buttons.length) % buttons.length

  event.preventDefault()
  buttons[next]?.focus()
}

export const DesktopToolbar = (): ReactNode => {
  const visible = useVisibleModules({ includeTabs: true })
  const workspace = useWorkspace()

  const entries = TOOLBAR.map((module) =>
    visible.find((entry) => entry.module === module)
  ).filter((entry): entry is ModuleEntry => entry !== undefined)

  if (entries.length === 0) return null

  return (
    <div
      role="toolbar"
      aria-label="Telas mais usadas"
      onKeyDown={moveFocus}
      className="flex h-12 shrink-0 items-center gap-0.5 overflow-x-auto border-b border-(--border-default) bg-(--surface) px-1.5"
    >
      {entries.map(({ module, label, Icon }, index) => (
        <Tooltip
          key={module}
          content={`${label} · Shift+clique abre outra janela`}
          side="bottom"
        >
          <button
            type="button"
            // Um só ponto de parada do Tab na barra: as setas fazem o resto.
            tabIndex={index === 0 ? 0 : -1}
            onClick={(event) => {
              workspace.open({ module, newInstance: event.shiftKey })
            }}
            className={cn(
              'flex h-10 min-w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-(--radius-sm) px-2',
              'text-(length:--text-xs) text-(--text-secondary)',
              'transition-colors duration-(--duration-fast)',
              'hover:bg-(--surface-hover) hover:text-(--text-primary)',
              'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--border-focus)'
            )}
          >
            <Icon
              aria-hidden="true"
              className="h-[18px] w-[18px] text-(--brand-primary)"
            />
            <span className="max-w-20 truncate">{label}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  )
}
